import { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";

export type PrimaryTimeframeValue =
  | "yesterday"
  | "two_days_ago"
  | "three_days_ago"
  | "mtd"
  | "custom";

export type CompareTimeframeValue =
  | "yesterday"
  | "two_days_ago"
  | "three_days_ago";

export interface CommittedSelection {
  primary: PrimaryTimeframeValue;
  customStart: Dayjs | null;
  customEnd: Dayjs | null;
  compareEnabled: boolean;
  compare: CompareTimeframeValue | null;
}

export const fmt = (d: Dayjs) => d.format("DD/MM/YYYY");

export const RELATIVE_DAY_OPTIONS: {
  value: CompareTimeframeValue;
  days: number;
}[] = [
  { value: "yesterday", days: 1 },
  { value: "two_days_ago", days: 2 },
  { value: "three_days_ago", days: 3 },
];

const ALL_COMPARE_CANDIDATES: CompareTimeframeValue[] = RELATIVE_DAY_OPTIONS.map(
  (o) => o.value,
);

export const relativeDayLabel = (value: CompareTimeframeValue): string => {
  const opt = RELATIVE_DAY_OPTIONS.find((o) => o.value === value)!;
  const date = fmt(dayjs().subtract(opt.days, "day"));
  const prefix = opt.days === 1 ? "Yesterday" : `${opt.days} Days Ago`;
  return `${prefix} (${date})`;
};

export const getPrimaryLabel = (
  primary: PrimaryTimeframeValue,
  customStart: Dayjs | null,
  customEnd: Dayjs | null,
): string => {
  if (primary === "mtd") return "Month to Date";
  if (primary === "custom") {
    if (customStart && customEnd) {
      return `${fmt(customStart)} - ${fmt(customEnd)}`;
    }
    return "Custom Range";
  }
  return relativeDayLabel(primary);
};

export const getCompareLabel = (
  compare: CompareTimeframeValue | null,
): string => (compare ? relativeDayLabel(compare) : "");

export interface PrimaryListOption {
  value: PrimaryTimeframeValue;
  listLabel: string;
}

export const PRIMARY_LIST_OPTIONS: PrimaryListOption[] = [
  { value: "yesterday", listLabel: "Yesterday" },
  { value: "two_days_ago", listLabel: "2 days ago" },
  { value: "three_days_ago", listLabel: "3 days ago" },
  { value: "mtd", listLabel: "Month to date" },
  { value: "custom", listLabel: "Custom range" },
];

export const isCompareDisabledForPrimary = (
  primary: PrimaryTimeframeValue,
): boolean => primary === "mtd" || primary === "custom";

export interface UseTimeFrameCompareStateArgs {
  value: CommittedSelection;
  onApply: (next: CommittedSelection) => void;
  hasData: boolean;
  // Desktop shows the option list and the custom-range calendar together
  // (two panes), so "finish editing the calendar" and "close the popover"
  // are the same action — the default here. Mobile shows only one view at
  // a time in its Drawer; finishing the calendar there should return to the
  // option list instead of closing, so mobile passes `false`.
  dismissOnCustomExit?: boolean;
  // Which relative-day values are offered as Compare-to choices. Defaults
  // to all three (desktop); mobile restricts this to just two days ago /
  // three days ago.
  compareCandidates?: CompareTimeframeValue[];
  // Whether the currently-selected primary is dropped from the Compare-to
  // choices (desktop's behavior, since comparing a day to itself is a
  // no-op). Mobile always shows its full `compareCandidates` set instead,
  // even if one of them is also the current primary.
  excludeCurrentPrimaryFromCompare?: boolean;
}

// Shared staged-selection state machine — both the desktop popover and the
// mobile drawer show the same 5-option list + Compare-to + Custom-range
// calendar and must apply the exact same rules:
//  - Yesterday / 2 days ago / 3 days ago / Month to date apply immediately
//    (and close the host overlay).
//  - Custom range stages until Apply/Cancel (needs both dates picked).
//  - Compare-to: turning it ON stages until Apply; turning it OFF (or
//    switching to MTD/Custom, which always disables it) applies immediately.
// Each caller renders its own chrome (Popper vs Drawer) around `open`.
export function useTimeFrameCompareState({
  value,
  onApply,
  hasData,
  dismissOnCustomExit = true,
  compareCandidates = ALL_COMPARE_CANDIDATES,
  excludeCurrentPrimaryFromCompare = true,
}: UseTimeFrameCompareStateArgs) {
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<CommittedSelection>(value);
  // Which sub-view is showing while `open` — only meaningful for callers
  // with a single-view layout (mobile); desktop shows the calendar
  // alongside the list whenever `isCustomPending` and never reads this.
  const [view, setView] = useState<"list" | "calendar">("list");

  const isCompareDisabled = isCompareDisabledForPrimary(staged.primary) || !hasData;

  const compareOptions = useMemo(
    () =>
      RELATIVE_DAY_OPTIONS.filter(
        (o) =>
          compareCandidates.includes(o.value) &&
          (!excludeCurrentPrimaryFromCompare || o.value !== staged.primary),
      ).map((o) => ({ value: o.value, label: relativeDayLabel(o.value) })),
    [staged.primary, compareCandidates, excludeCurrentPrimaryFromCompare],
  );

  const isCustomPending = staged.primary === "custom";
  const isComparePending = staged.compareEnabled;
  const isApplyEnabled = isCustomPending
    ? !!(staged.customStart && staged.customEnd)
    : isComparePending
      ? !!staged.compare
      : true;

  const handleOpen = () => {
    // Always resurface the option list on open, even if Custom range is the
    // current selection — the calendar is only entered by explicitly
    // tapping "Custom range" (see handleSelectPrimary), not merely because
    // it's already the active choice.
    setStaged(value);
    setView("list");
    setOpen(true);
  };

  // Hard close: the drawer/popover's own dismiss action (X button, outside
  // click, Escape) — always closes fully, regardless of which view is
  // showing. Deliberately leaves `staged` alone; resetting it here would
  // flip the visible content while the close transition plays, and
  // `handleOpen` already reinitializes `staged` on the next open anyway.
  const handleDiscard = () => {
    setOpen(false);
  };

  // Apply always fully closes — from the option list (confirms Compare-to)
  // or from the custom-range calendar (confirms the picked dates). Neither
  // case returns to an intermediate view.
  const handleApply = () => {
    if (!isApplyEnabled) return;
    onApply(staged);
    setOpen(false);
  };

  // Leaving the custom-range calendar without applying (its own Cancel
  // button). Desktop closes the whole popover; mobile discards the edit and
  // returns to the option list, keeping the drawer open.
  const handleCancelCustom = () => {
    if (dismissOnCustomExit) {
      setOpen(false);
      return;
    }
    setStaged(value);
    setView("list");
  };

  const handleSelectPrimary = (primary: PrimaryTimeframeValue) => {
    // Switching the main date range always clears any active Compare-to —
    // a comparison picked for one primary isn't assumed to still make
    // sense against a different one, so it's never carried over.
    const next: CommittedSelection = {
      primary,
      customStart: primary === "custom" ? staged.customStart : null,
      customEnd: primary === "custom" ? staged.customEnd : null,
      compareEnabled: false,
      compare: null,
    };
    setStaged(next);
    if (primary === "custom") {
      // Needs a date range picked before Apply — show the calendar.
      setView("calendar");
      return;
    }
    // Yesterday / 2 days ago / 3 days ago / Month to date take effect right
    // away and close the overlay.
    onApply(next);
    setOpen(false);
  };

  const handleCompareToggle = (checked: boolean) => {
    if (checked) {
      // Turning it on just stages the intent — Apply is required once a
      // comparison value is chosen.
      setStaged((prev) => ({ ...prev, compareEnabled: true }));
      return;
    }
    // Turning it off is a simple reset, applied immediately like a
    // predefined option so it can't be left half-discarded on close.
    const next: CommittedSelection = {
      ...staged,
      compareEnabled: false,
      compare: null,
    };
    setStaged(next);
    onApply(next);
  };

  const setCustomRange = (start: Dayjs | null, end: Dayjs | null) => {
    setStaged((prev) => ({ ...prev, customStart: start, customEnd: end }));
  };

  const setCompareValue = (compare: CompareTimeframeValue | null) => {
    setStaged((prev) => ({ ...prev, compare }));
  };

  return {
    open,
    view,
    staged,
    isCustomPending,
    isComparePending,
    isApplyEnabled,
    isCompareDisabled,
    compareOptions,
    handleOpen,
    handleDiscard,
    handleApply,
    handleCancelCustom,
    handleSelectPrimary,
    handleCompareToggle,
    setCustomRange,
    setCompareValue,
  };
}
